import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { api, errText } from '@/lib/api';
import { errorContext, correlationId } from '@/lib/errorContext';
import { useApp } from '@/context/AppContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { toast } from 'sonner';
import { HelpCircle, Loader2, Send, Sparkles, LifeBuoy, Bug, BookOpen } from 'lucide-react';

/** Tiny markdown renderer for the built-in guides (headings + bullets only). */
export function GuideText({ markdown }) {
  const lines = String(markdown || '').split('\n');
  return (
    <div className="space-y-1.5">
      {lines.map((ln, i) => {
        const t = ln.trim();
        if (!t) return null;
        if (t.startsWith('# ')) return <h3 key={i} className="text-base font-semibold pt-1">{t.slice(2)}</h3>;
        if (t.startsWith('## ')) return <h4 key={i} className="text-sm font-semibold pt-2">{t.slice(3)}</h4>;
        if (t.startsWith('- ')) return <p key={i} className="text-sm text-muted-foreground pl-4 relative before:content-['•'] before:absolute before:left-1">{t.slice(2).replaceAll('**', '')}</p>;
        return <p key={i} className="text-sm text-muted-foreground">{t.replaceAll('**', '')}</p>;
      })}
    </div>
  );
}

function AskTab({ role }) {
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [thread, setThread] = useState([]); // {q, a}

  const ask = async () => {
    const question = q.trim();
    if (question.length < 4) { toast.error('Type a question first'); return; }
    setBusy(true);
    try {
      const r = await api.post('/help/ask', { question, role });
      setThread((t) => [...t, { q: question, a: r.data.answer }]);
      setQ('');
    } catch (e) {
      toast.error(errText(e, 'The assistant is briefly unavailable'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="help-ask-tab">
      <p className="text-xs text-muted-foreground">
        Instant answers about how to use the platform. Please don't include patient names or medical details — they're never needed here.
      </p>
      <div className="space-y-3 max-h-[38vh] overflow-y-auto pr-1">
        {thread.map((m, i) => (
          <div key={i} className="space-y-1.5">
            <p className="text-sm font-medium">{m.q}</p>
            {/* The prompt asks for plain text, but models drift back to `##`/`**`.
                Render through the guide's renderer so a stray symbol reads as a
                heading instead of showing up literally. */}
            <div className="rounded-lg bg-secondary/60 border px-3 py-2 text-sm" data-testid={`help-ai-answer-${i}`}><GuideText markdown={m.a} /></div>
          </div>
        ))}
        {thread.length === 0 && (
          <div className="text-center py-6 text-muted-foreground">
            <Sparkles className="h-6 w-6 mx-auto mb-1 opacity-40" />
            <p className="text-xs">e.g. "How do I send a patient a new link?"</p>
          </div>
        )}
      </div>
      <div className="flex gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && !busy && ask()} placeholder="Ask how to do something…" data-testid="help-ask-input" />
        <Button onClick={ask} disabled={busy} size="icon" data-testid="help-ask-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
}

function SupportTab() {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [category, setCategory] = useState('general');
  const [busy, setBusy] = useState(false);
  const [mine, setMine] = useState([]);

  const loadMine = () => api.get('/help/tickets/mine').then((r) => setMine(r.data)).catch(() => {});
  useEffect(() => { loadMine(); }, []);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post('/help/tickets', { subject, message, category });
      toast.success('Message sent — our support team will reply here.');
      setSubject(''); setMessage('');
      loadMine();
    } catch (e) {
      toast.error(errText(e, 'Could not send your message'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="help-support-tab">
      <div className="space-y-2">
        <div className="flex gap-2">
          <Input placeholder="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="help-ticket-subject" />
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-32 shrink-0" data-testid="help-ticket-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              {['general', 'account', 'billing', 'technical'].map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Textarea rows={3} placeholder="Describe what you need help with…" value={message} onChange={(e) => setMessage(e.target.value)} data-testid="help-ticket-message" />
        <Button onClick={submit} disabled={busy || !subject.trim() || !message.trim()} className="w-full" data-testid="help-ticket-submit">
          {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Send to support
        </Button>
      </div>
      {mine.length > 0 && (
        <div className="space-y-2 pt-1">
          <Label className="text-xs text-muted-foreground">Your recent tickets</Label>
          <div className="space-y-2 max-h-[30vh] overflow-y-auto pr-1" data-testid="help-my-tickets">
            {mine.map((t) => (
              <div key={t.id} className="rounded-lg border px-3 py-2 space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium truncate">{t.subject}</span>
                  <Badge variant="outline" className="text-xs capitalize shrink-0">{String(t.status).replace('_', ' ')}</Badge>
                </div>
                {(t.replies || []).map((r, i) => (
                  <p key={i} className="text-xs rounded bg-secondary/60 px-2 py-1">Support: {r.message}</p>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function FeedbackTab() {
  const { pathname } = useLocation();
  const [type, setType] = useState('feature');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState('medium');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      // The page, the build and this sitting's reference. `errorContext` sends
      // the matched route PATTERN, never the URL — see lib/errorContext.js.
      await api.post('/help/feedback', {
        type, title, message,
        severity: type === 'bug' ? severity : '',
        context: errorContext(pathname, { kind: 'manual' }),
      });
      toast.success(type === 'bug' ? 'Bug report sent — thank you!' : 'Feature request sent — thank you!');
      setTitle(''); setMessage('');
    } catch (e) {
      toast.error(errText(e, 'Could not send feedback'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2" data-testid="help-feedback-tab">
      <p className="text-xs text-muted-foreground">Tell us what you need or what's broken — it goes straight to the product team.</p>
      <div className="flex gap-2">
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="w-40 shrink-0" data-testid="help-feedback-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="feature">Feature request</SelectItem>
            <SelectItem value="bug">Bug / issue</SelectItem>
          </SelectContent>
        </Select>
        {type === 'bug' && (
          <Select value={severity} onValueChange={setSeverity}>
            <SelectTrigger data-testid="help-feedback-severity"><SelectValue /></SelectTrigger>
            <SelectContent>
              {['low', 'medium', 'high'].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>
      <Input placeholder={type === 'bug' ? 'What is broken?' : 'What would you like us to build?'} value={title} onChange={(e) => setTitle(e.target.value)} data-testid="help-feedback-title" />
      <Textarea rows={3} placeholder="Details…" value={message} onChange={(e) => setMessage(e.target.value)} data-testid="help-feedback-message" />
      <Button onClick={submit} disabled={busy || !title.trim() || !message.trim()} className="w-full" data-testid="help-feedback-submit">
        {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Send
      </Button>
      <p className="text-xs text-muted-foreground" data-testid="help-feedback-context">
        We attach the page you're on and the app version so we can find it — never
        a patient link. Reference <span className="font-mono">{correlationId()}</span>.
      </p>
    </div>
  );
}

function GuideTab({ role }) {
  const [md, setMd] = useState('');
  useEffect(() => {
    api.get(`/help/guide/${role === 'owner' ? 'admin' : role}`).then((r) => setMd(r.data.markdown)).catch(() => {});
  }, [role]);
  return (
    <div className="max-h-[48vh] overflow-y-auto pr-1" data-testid="help-guide-tab">
      <GuideText markdown={md} />
    </div>
  );
}

/**
 * Floating "Need help?" bubble mounted in the staff AppShell.
 * AI-first assistance grounded in the built-in guides, with escalation to the
 * platform support desk and a feature-request / bug-report channel.
 */
export function HelpBubble() {
  const { role } = useApp();
  const [open, setOpen] = useState(false);
  if (!role) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-24 right-5 z-40 h-12 w-12 rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/25 grid place-items-center hover:opacity-90 focus-visible:ring-2 focus-visible:ring-ring"
        aria-label="Help and support"
        data-testid="help-bubble-button"
      >
        <HelpCircle className="h-6 w-6" />
      </button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto" data-testid="help-bubble-panel">
          <SheetHeader className="text-left">
            <SheetTitle className="flex items-center gap-2"><LifeBuoy className="h-4 w-4 text-primary" /> Help & support</SheetTitle>
            <SheetDescription>Ask the assistant, read the guide, reach support — or report a bug / request a feature.</SheetDescription>
          </SheetHeader>
          <Tabs defaultValue="ask" className="mt-4">
            <TabsList className="grid grid-cols-4 w-full">
              <TabsTrigger value="ask" data-testid="help-tab-ask"><Sparkles className="h-3.5 w-3.5 mr-1" /> Ask</TabsTrigger>
              <TabsTrigger value="guide" data-testid="help-tab-guide"><BookOpen className="h-3.5 w-3.5 mr-1" /> Guide</TabsTrigger>
              <TabsTrigger value="support" data-testid="help-tab-support"><LifeBuoy className="h-3.5 w-3.5 mr-1" /> Support</TabsTrigger>
              <TabsTrigger value="feedback" data-testid="help-tab-feedback"><Bug className="h-3.5 w-3.5 mr-1" /> Report</TabsTrigger>
            </TabsList>
            <TabsContent value="ask" className="mt-4"><AskTab role={role} /></TabsContent>
            <TabsContent value="guide" className="mt-4"><GuideTab role={role} /></TabsContent>
            <TabsContent value="support" className="mt-4"><SupportTab /></TabsContent>
            <TabsContent value="feedback" className="mt-4"><FeedbackTab /></TabsContent>
          </Tabs>
        </SheetContent>
      </Sheet>
    </>
  );
}
